import React, {FC} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {AppRoutesPrefixesEnum} from '../../../../../../../../src/components/appRoutesPrefixes';
import {ResolveDispatchProps} from '../../../../../../../../src/helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../src/helpers/reselectHelpers';
import {CollapsedItemKeyPrefixesEnum} from '../../../../../../../../src/models/api/localPreferencesModel';
import {toggleExpandItemAction} from '../../../../../../../../src/store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../../../../../../../src/store/data/localPreferences/localPreferencesSelectors';
import {getUserNamespace} from '../../../../../../../../src/store/data/teammates/teammatesSelectors';
import {
  makeGetNamespace,
  makeGetSelectedCardIds,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {navigateToLinkAction} from '../../../../../../../../src/store/stateless/navigationActions';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {PrivateContactsPage} from './privateContactsPage';

/*
 * Constants.
 */

const infoBoxExpandedKey = `${CollapsedItemKeyPrefixesEnum.PRIVATE_CONTACTS}infoBox`;
const isInfoBoxExpandedDefault = true;

/*
 * Props.
 */

interface PrivateContactsPageContainerPublicProps {
  isShowingImportCSVDialog: boolean;
  hasRunningImportContactsCSVJobs: boolean;
  onToggleImportCSVClick: Handler;
}
type PrivateContactsPageContainerComponentProps = PrivateContactsPageContainerPublicProps &
  ResolveStateProps<typeof mapStateToProps> &
  ResolveDispatchProps<typeof mapDispatchToProps>;

/*
 * Selectors.
 */

const getIsInfoBoxExpanded = createSelector(getFindIsExpanded, (findIsExpanded) =>
  findIsExpanded(infoBoxExpandedKey, isInfoBoxExpandedDefault),
);

/*
 * Component.
 */

const PrivateContactsPageContainerComponent: FC<PrivateContactsPageContainerComponentProps> = (props) => {
  const {
    namespace,
    userNamespace,
    selectedCardIds,
    isShowingImportCSVDialog,
    isInfoBoxExpanded,
    hasRunningImportContactsCSVJobs,
    navigateToLink,
    onToggleInfoBox,
    onToggleImportCSVClick,
  } = props;

  useInitialEffect(() => {
    if (namespace !== userNamespace) {
      navigateToLink(`/${AppRoutesPrefixesEnum.CONTACTS_MANAGER}`);
    }
  });

  if (!namespace) {
    return null;
  }

  return (
    <PrivateContactsPage
      namespace={namespace}
      selectedCardIds={selectedCardIds}
      isInfoBoxExpanded={isInfoBoxExpanded}
      isShowingImportCSVDialog={isShowingImportCSVDialog}
      hasRunningImportContactsCSVJobs={hasRunningImportContactsCSVJobs}
      onToggleInfoBox={onToggleInfoBox}
      onToggleImportCSVClick={onToggleImportCSVClick}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  namespace: makeGetNamespace(),
  selectedCardIds: makeGetSelectedCardIds(),
  userNamespace: getUserNamespace,
  isInfoBoxExpanded: getIsInfoBoxExpanded,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  navigateToLink: navigateToLinkAction,
  onToggleInfoBox: () => toggleExpandItemAction(infoBoxExpandedKey, isInfoBoxExpandedDefault),
}));

export const PrivateContactsPageContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(PrivateContactsPageContainerComponent);
